import React from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Input } from "@cms/ui/components/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@cms/ui/components/select";
import {
  hasChildren,
  linkMode,
  menuItemSelfProblem,
  moveItem,
  type MenuItem,
} from "../lib/menus";

// Sentinel Select values. Page options use the slug itself (page slugs are
// lowercase-only, so they can't collide with these).
const CUSTOM_URL = "__custom_url__";
const NO_LINK = "__no_link__";

export interface PageOption {
  slug: string;
  title: string;
  published?: number;
}

interface MenuItemRowProps {
  item: MenuItem;
  index: number;
  count: number;
  pages: PageOption[];
  // Child rows are leaves: no "no link" heading option and no further nesting.
  isChild?: boolean;
  onChange: (item: MenuItem) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}

export function MenuItemRow({
  item,
  index,
  count,
  pages,
  isChild = false,
  onChange,
  onMove,
  onRemove,
}: MenuItemRowProps) {
  const mode = linkMode(item);
  const problem = menuItemSelfProblem(item);
  // A page item whose page was deleted (or never existed on this older
  // tenant): keep it selectable so the row isn't stuck, but flag it.
  const pageMissing = mode === "page" && !pages.some((p) => p.slug === item.page);
  const selectValue = mode === "page" ? item.page : mode === "url" ? CUSTOM_URL : NO_LINK;

  // Changing the link mode preserves the label and any existing sub-menu.
  const setLink = (value: string) => {
    if (value === CUSTOM_URL) {
      onChange({ label: item.label, url: item.url ?? "", children: item.children });
    } else if (value === NO_LINK) {
      onChange({ label: item.label, children: item.children });
    } else {
      onChange({ label: item.label, page: value, children: item.children });
    }
  };

  const children = item.children ?? [];
  const setChildren = (next: MenuItem[]) => onChange({ ...item, children: next });

  return (
    <div className="border-b py-3 last:border-0">
      <div className="flex items-start gap-2">
        <Input
          value={item.label}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...item, label: e.target.value })}
          placeholder="Label"
          aria-label="Item label"
          className="w-44"
        />
        <Select value={selectValue} onValueChange={setLink}>
          <SelectTrigger className="w-52" aria-label="Item link">
            <SelectValue placeholder="Link to…" />
          </SelectTrigger>
          <SelectContent>
            {pages.map((page) => (
              <SelectItem key={page.slug} value={page.slug}>
                {page.title} {page.published ? "" : "(draft)"}
              </SelectItem>
            ))}
            {pageMissing && item.page && (
              <SelectItem value={item.page}>/{item.page} (page not found)</SelectItem>
            )}
            <SelectItem value={CUSTOM_URL}>Custom URL…</SelectItem>
            {!isChild && <SelectItem value={NO_LINK}>No link (dropdown heading)</SelectItem>}
          </SelectContent>
        </Select>
        {mode === "url" && (
          <Input
            value={item.url ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ label: item.label, url: e.target.value, children: item.children })
            }
            placeholder="https://…"
            aria-label="Item URL"
            className="flex-1"
          />
        )}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <Button variant="ghost" size="sm" disabled={index === 0} aria-label="Move up" onClick={() => onMove(-1)}>
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" disabled={index === count - 1} aria-label="Move down" onClick={() => onMove(1)}>
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            aria-label="Remove item"
            onClick={onRemove}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {problem ? (
        <p className="mt-1.5 text-xs text-destructive">{problem}</p>
      ) : pageMissing ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          This page doesn't exist yet — the link stays hidden on your site until you create and publish it.
        </p>
      ) : mode === "page" && !pages.find((p) => p.slug === item.page)?.published ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Draft page — the link appears on your site once the page is published.
        </p>
      ) : mode === "none" && hasChildren(item) ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Dropdown heading — opens its sub-links instead of navigating.
        </p>
      ) : null}

      {/* Sub-menu editor: top-level items only (children are leaves). */}
      {!isChild && (
        <div className="ml-6 mt-2 border-l pl-4">
          {children.map((child, ci) => (
            <MenuItemRow
              key={ci}
              item={child}
              index={ci}
              count={children.length}
              pages={pages}
              isChild
              onChange={(next) => {
                const updated = children.slice();
                updated[ci] = next;
                setChildren(updated);
              }}
              onMove={(delta) => setChildren(moveItem(children, ci, delta))}
              onRemove={() => setChildren(children.filter((_, i) => i !== ci))}
            />
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="mt-1 text-muted-foreground"
            onClick={() => setChildren([...children, { label: "", url: "" }])}
          >
            <Plus className="h-3.5 w-3.5" />
            Add sub-link
          </Button>
        </div>
      )}
    </div>
  );
}
